import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Clock, MapPin, Phone, ArrowRight, Star } from 'lucide-react';

const SERVICES = [
  { 
    id: 'refresh_detail', 
    name: 'Refresh Detail', 
    description: 'Exterior hand wash, wheels & tires, interior vacuum & wipe-down, windows inside and out.', 
    duration: '1.5-2 hours', 
    startPrice: 199, 
    image: 'https://images.pexels.com/photos/6872150/pexels-photo-6872150.jpeg',
    popular: false
  },
  { 
    id: 'full_detail', 
    name: 'Full Detail', 
    description: 'Deep wash, trim & rim detail, interior deep cleaning, shampoo seats & carpet, paint sealant.', 
    duration: '3-4 hours', 
    startPrice: 349, 
    image: 'https://images.pexels.com/photos/6872152/pexels-photo-6872152.jpeg',
    popular: true
  }
];

const GALLERY_IMAGES = [
  { url: 'https://customer-assets.emergentagent.com/job_detail-onthego/artifacts/tji6u4kb_Front.jpeg', alt: 'BMW 3 Series - Exterior Detail', category: 'Exterior' },
  { url: 'https://customer-assets.emergentagent.com/job_detail-onthego/artifacts/xx9stjyj_Interior%20cropped%202.jpeg', alt: 'BMW Interior - Premium Detail', category: 'Interior' },
  { url: 'https://customer-assets.emergentagent.com/job_detail-onthego/artifacts/dhq8ra0m_Range%201.jpeg', alt: 'Range Rover - Full Detail', category: 'Exterior' },
  { url: 'https://customer-assets.emergentagent.com/job_detail-onthego/artifacts/fd8bsn9v_Range%203.jpeg', alt: 'Interior Cleaning Process', category: 'Interior' },
  { url: 'https://customer-assets.emergentagent.com/job_detail-onthego/artifacts/m43wyafz_Range%204.jpeg', alt: 'Range Rover Interior - After', category: 'Interior' }
];

export default function Home() {
  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight text-[#C9A84C]" data-testid="logo">
            SAC CITY SHINE
          </Link>
          <Link to="/booking">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-medium transition-all duration-200 active:scale-[0.98]" data-testid="nav-book-btn">
              Book Now
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center luxury-image" style={{ backgroundImage: 'url(https://images.pexels.com/photos/15381565/pexels-photo-15381565.jpeg)' }} />
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto animate-fade-up">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Luxury Mobile Auto Detailing in Sacramento
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-10 font-body">
            We come to you — home or work
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200 rounded-sm px-8 py-6 text-base font-medium transition-all duration-200 active:scale-[0.98]" data-testid="hero-book-btn">
              Book an Appointment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Business Info Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black" data-testid="business-info-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8">Professional Mobile Detailing</h2>
          <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-12 max-w-2xl mx-auto">
            Sac City Shine is a professional mobile detailing service serving the Sacramento area. 
            We bring the detail shop to you with premium products and meticulous attention to every surface.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-surface border border-[#262626] p-8 hover:border-white transition-colors duration-300" data-testid="hours-card">
              <Clock className="w-8 h-8 text-white mb-4 mx-auto" />
              <h3 className="font-heading text-lg font-medium text-white mb-2">Hours</h3>
              <p className="text-gray-400 text-sm">Mon, Sat & Sun</p>
              <p className="text-white font-medium">7:00 AM – 5:00 PM</p>
            </div>
            
            <div className="bg-surface border border-[#262626] p-8 hover:border-white transition-colors duration-300" data-testid="location-card">
              <MapPin className="w-8 h-8 text-white mb-4 mx-auto" />
              <h3 className="font-heading text-lg font-medium text-white mb-2">Service Area</h3>
              <p className="text-gray-400 text-sm">Sacramento & Surrounding</p>
              <p className="text-white font-medium">We Come to You</p>
            </div>
            
            <div className="bg-surface border border-[#262626] p-8 hover:border-white transition-colors duration-300" data-testid="contact-card">
              <Phone className="w-8 h-8 text-white mb-4 mx-auto" />
              <h3 className="font-heading text-lg font-medium text-white mb-2">Contact</h3>
              <p className="text-gray-400 text-sm">Questions?</p>
              <p className="text-white font-medium">Book Online</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-surface" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-gray-400 text-base md:text-lg">Premium detailing packages tailored to your needs</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((service) => (
              <Link key={service.id} to={`/booking?service=${service.id}`} className="group" data-testid={`service-card-${service.id}`}>
                <div className="relative h-[420px] bg-black border border-[#262626] overflow-hidden hover:border-white transition-all duration-300">
                  {service.popular && (
                    <div className="absolute top-4 right-4 z-10 bg-white text-black px-3 py-1 text-xs font-bold flex items-center gap-1">
                      <Star className="h-3 w-3 fill-current" /> MOST POPULAR
                    </div>
                  )}
                  <div className="absolute inset-0 bg-cover bg-center luxury-image transition-transform duration-500 group-hover:scale-105" style={{ backgroundImage: `url(${service.image})` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading text-xl font-bold text-white">{service.name}</h3>
                    </div>
                    <p className="text-white text-2xl font-bold mb-2">From ${service.startPrice}</p>
                    <p className="text-gray-300 text-sm mb-3">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">{service.duration}</span>
                      <span className="text-white text-sm flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Book Now <ArrowRight className="ml-1 h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Add-On Services */}
          <div className="mt-16 text-center">
            <h3 className="font-heading text-2xl font-bold text-white mb-8">Add-On Services</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { name: 'Shampoo Seats', price: '$40' },
                { name: 'Shampoo Carpets', price: '$40' },
                { name: 'Engine Bay Detail', price: '$40' },
                { name: 'Pet Hair Removal', price: 'Starting at $50' },
                { name: 'Clay Bar Treatment', price: '$50' },
                { name: 'One-Step Polish', price: 'Starting at $150' },
                { name: 'Ceramic Spray Sealant Upgrade', price: '$50' }
              ].map((addon) => (
                <div key={addon.name} className="bg-black border border-[#262626] px-5 py-4 hover:border-white transition-colors text-left">
                  <p className="text-white font-medium text-sm">{addon.name}</p>
                  <p className="text-gray-400 text-sm mt-1">{addon.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Our Work</h2>
            <p className="text-gray-400 text-base md:text-lg">Real results from real customers in Sacramento</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY_IMAGES.map((image, index) => (
              <div 
                key={index} 
                className={`relative overflow-hidden border border-[#262626] hover:border-white transition-all duration-300 ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                data-testid={`gallery-image-${index}`}
              >
                <div className={`${index === 0 ? 'aspect-square md:aspect-auto md:h-full' : 'aspect-square'}`}>
                  <img 
                    src={image.url} 
                    alt={image.alt}
                    className="w-full h-full object-cover luxury-image hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <span className="text-white text-sm font-medium">{image.category}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-surface border-t border-[#262626]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">Ready to Shine?</h2>
          <p className="text-gray-400 text-base md:text-lg mb-10">
            Book your mobile detail appointment today and experience the Sac City difference.
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200 rounded-sm px-10 py-6 text-base font-medium transition-all duration-200 active:scale-[0.98]" data-testid="cta-book-btn">
              Schedule Your Detail
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 bg-black border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Sac City Shine. Sacramento, CA.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
            <span className="text-gray-500 text-sm">Mon, Sat & Sun: 7AM – 5PM</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
